/**
 * -----------------------------------------------------------------------------
 * Driver Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single driver of the organization.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLoggedInUserData } from "../../../utils/user";
import HeaderView from "../../../components/admin/headerView";
import { APP_CONFIG } from "../../../constants/constants";
import AppSearchBox from "../../../components/searchBox";
import { useDebouncedCallback } from "use-debounce";
import { routeUrls } from "../../../navigation/routeUrls";
import Accordian from "../../../components/accordian";
import { useFormik } from "formik";
import {
  driverDetailsInitialValues,
  driverDetailsYupValidationSchema,
  driverUploadFormInitialValues,
  driverUploadFormValidationSchema,
} from "./validation";
import {
  DriverGeneralDetailForm,
  DriverLicenseDetailForm,
  DriverMedicalDetailForm,
} from "./driver-form";
import {
  useDeleteSingleDriverMutation,
  useEditOrganizationDriverImageMutation,
  useEditOrganizationDriverMutation,
  useOrganizationDriversQuery,
  useSingleOrganizationDriverQuery,
} from "../../../api/network/adminApiServices";
import {
  SingleOrganizationDriverPayload,
  TEditOrganizationDriverPayloadData,
  TLegacyDriver,
} from "../../../api/types/Driver";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import { useDispatch, useSelector } from "react-redux";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import {
  dateToGregorianDays,
  gregorianDaysToJsDate,
} from "../../../utils/dateTime";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { RESPONSE_CODE } from "../../../api/network/constants";
import svgIcons from "../../../assets/svg/svgIcons";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";

const ScreenAdminDetailDriver = () => {
  const { driverId } = useParams<{ driverId: any }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.drivers",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.drivers.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { getUserACLAccessLevel } = useACL();

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { drivers: orgDriversQueryParams } = listingQueryParams;

  // flag to idenfiy if driver is coming from create new driver popup
  const isNewEntity = useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );

  // prepare query params for fetching organization drivers
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const thisOrgDriverGroupAllowed = useLoggedInUserData("hasdrivers");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        drivers: { ...orgDriversQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization drivers
  const { data: dataOrgDrivers, isFetching: isFetchingOrgDrivers } =
    useOrganizationDriversQuery(orgDriversQueryParams);
  const { results, count } = dataOrgDrivers || {};

  // fetch single driver details
  const {
    data: dataSingleDriver,
    isFetching: isFetchingSingleDriver,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationDriverQuery(
    {
      account_id: thisUserOrganizationId,
      driver_id: driverId,
    } as SingleOrganizationDriverPayload,
    {
      skip: !driverId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
      ) {
        toast.error(t("toast.driver_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.drivers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isErrorSingleEntity, errorSingleEntity]);

  // driver mutations
  const [editOrganizationDriverApiTrigger, { isLoading: isLoadingEditDriver }] =
    useEditOrganizationDriverMutation();
  const [deleteSingleDriverApiTrigger, { isLoading: isLoadingDeleteDriver }] =
    useDeleteSingleDriverMutation();
  const [
    editOrganizationDriverImageAPITrigger,
    { isLoading: isLoadingEditDriverImage },
  ] = useEditOrganizationDriverImageMutation();

  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleDriver);
  }, [isFetchingSingleDriver]);

  const formik = useFormik({
    initialValues: driverDetailsInitialValues,
    validationSchema: driverDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const data: TEditOrganizationDriverPayloadData = {
        accountid: thisUserOrganizationId,
        driverid: values.driver_id,
        address: values.address,
        badgeid: values.badge_employee_id,
        cardid: values.card_id,
        contactemail: values.contact_email,
        isactive: values.is_active ? 1 : 0,
        hazmatcert: values.hazmat_certified ? 1 : 0,
        licenseexpire: !!values.license_expiry_date
          ? dateToGregorianDays(new Date(values.license_expiry_date))
          : 0,
        licensenumber: values.license_number,
        licensestate: values.license_state,
        licensestatus: values.license_status,
        licensetype: values.license_type,
        medcardexpdate: !!values.medical_card_expiry_date
          ? dateToGregorianDays(new Date(values.medical_card_expiry_date))
          : 0,
        medicalcardno: values.medical_card_no,
        description: values.driver_name,
        displayname: values.driver_name,
        contactphone: values.contact_phone,
        twic: values.twic,
        twicexpdate: !!values.twic_expiry_date
          ? dateToGregorianDays(new Date(values.twic_expiry_date))
          : 0,
        deviceid: values.vehicle_id,
      };

      // call api for updating driver
      editOrganizationDriverApiTrigger({ data })
        .unwrap()
        .then(() => {
          toast.success(t("toast.driver_updated"));
          navigate(routeUrls.dashboardChildren.adminChildren.drivers);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        });
    },
  });
  const uploadFormik = useFormik({
    initialValues: {
      ...driverUploadFormInitialValues,
      driverid: driverId,
    },
    validationSchema: driverUploadFormValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      // intentionally disabled returning because this will allow
      // the removal of image that was previously set
      // --------------------------------------------------------
      // if (!values.image) {
      //   return;
      // }
      console.log("we are here");
      const formData = new FormData();
      formData.append("driverid", values.driverid);
      if (values.image) {
        formData.append("image", values?.image as File);
      }
      editOrganizationDriverImageAPITrigger(formData)
        .unwrap()
        .then((data) => {
          if (APP_CONFIG.DEBUG.ALL) console.log("RES DATA", data);
          if (!!data?.message) {
            toast.success(t("toast.driver_updated"), { autoClose: 10000 });
          } else {
            toast.error(t("toast.updation_failed"));
          }
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          toast.error(errors?.join(" "));
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleDriver) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        driver_id: dataSingleDriver?.driverid,
        driver_image_url: dataSingleDriver?.driverimageurl || "",
        driver_name: dataSingleDriver?.description || "",
        // nick_name: dataSingleDriver?.displayname || "", // not needed per client feedback
        contact_phone: dataSingleDriver?.contactphone,
        contact_email: dataSingleDriver?.contactemail || "",
        badge_employee_id: dataSingleDriver?.badgeid || "",
        card_id: dataSingleDriver?.cardid || "",
        is_active: dataSingleDriver?.isactive === 1,
        license_type: dataSingleDriver?.licensetype || "",
        license_state: dataSingleDriver?.licensestate || "",
        license_number: dataSingleDriver?.licensenumber || "",
        license_expiry_date: dataSingleDriver?.licenseexpire
          ? gregorianDaysToJsDate(dataSingleDriver?.licenseexpire).getTime()
          : 0,
        license_status: dataSingleDriver?.licensestatus || "",
        medical_card_no: dataSingleDriver?.medicalcardno || "",
        medical_card_expiry_date: !!dataSingleDriver?.medcardexpdate
          ? gregorianDaysToJsDate(dataSingleDriver?.medcardexpdate).getTime()
          : 0,
        hazmat_certified: dataSingleDriver?.hazmatcert === 1,
        twic: dataSingleDriver?.twic || "",
        twic_expiry_date: !!dataSingleDriver?.twicexpdate
          ? gregorianDaysToJsDate(dataSingleDriver?.twicexpdate).getTime()
          : 0,
        address: dataSingleDriver?.address || "",
        vehicle_id: dataSingleDriver?.deviceid || "",
      });
      uploadFormik.setValues({
        driverid: dataSingleDriver.driverid,
        image: null,
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [dataSingleDriver, driverId]);

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    formik;

  // handle delete driver
  const handleDeleteDriver = () => {
    deleteSingleDriverApiTrigger({
      account_id: `${thisUserOrganizationId}`,
      driver_id: driverId,
    })
      .unwrap()
      .then(() => {
        toast.success(t("toast.driver_deleted"));
        navigate(routeUrls.dashboardChildren.adminChildren.drivers);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  // handle edit driver
  const handleEditDriver = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  const onBackButtonClick = () =>
    navigate(routeUrls.dashboardChildren.adminChildren.drivers);

  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            ...(thisOrgDriverGroupAllowed === "1"
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "danger",
                    label: tMain("delete"),
                    onClick: () => {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showDeleteConfirmation: true,
                        })
                      );
                    },
                    disabled:
                      isFetchingSingleDriver ||
                      isLoadingEditDriver ||
                      isLoadingEditDriverImage ||
                      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER) <
                        ROLE_ACCESS_LEVELS.ALL,
                  } as FormActionButton,
                ]
              : []),

            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                    variant: "secondary",
                  } as FormActionButton,
                ]
              : []),

            ...(thisOrgDriverGroupAllowed === "1"
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "primary",
                    label: userCanEdit ? tMain("update") : tMain("edit"),
                    onClick: userCanEdit
                      ? handleEditDriver
                      : () => setUserCanEdit(!userCanEdit),
                    disabled:
                      isFetchingSingleDriver ||
                      isLoadingEditDriver ||
                      isLoadingEditDriverImage ||
                      (userCanEdit && !!Object.keys(errors).length) ||
                      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER) <
                        ROLE_ACCESS_LEVELS.WRITE,
                  } as FormActionButton,
                ]
              : []),
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleDriver,
    isLoadingEditDriver,
    isLoadingEditDriverImage,
    userCanEdit,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditDriver(),
              disabled: isLoadingEditDriver || !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditDriver, errors]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgDrivers ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgDriversQueryParams?.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {results?.map((item: TLegacyDriver, index: number) => (
                <AdminListingColumnItem
                  key={index}
                  selected={driverId === item.driverid}
                  onClick={() =>
                    navigate(
                      `${routeUrls.dashboardChildren.adminChildren.drivers}/${item.driverid}`
                    )
                  }
                  title={item.displayname || item.description}
                  description={item.badgeid}
                  asideText={item.contactphone || "-"}
                  bottomText={item.contactemail}
                />
              ))}
            </div>
            {!isFetchingOrgDrivers && (
              <Pagination
                pageSize={orgDriversQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      drivers: {
                        ...orgDriversQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count ? Math.ceil(count / orgDriversQueryParams.page_size) : 1
                }
                forcePage={orgDriversQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      drivers: {
                        ...orgDriversQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {(isFetchingSingleDriver || isLoadingEditDriverImage) && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditDriver}
                      disabled={isLoadingEditDriver || !!Object.keys(errors).length}
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {(isFetchingSingleDriver || isLoadingEditDriverImage) && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                  {thisOrgDriverGroupAllowed === "1" &&
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => { dispatch(setModalsData({ ...modalsState, showDeleteConfirmation: true })) }}
                      disabled={isFetchingSingleDriver || isLoadingEditDriver || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER) < ROLE_ACCESS_LEVELS.ALL}
                    />
                  }
                  </div> */}
                  {/* {userCanEdit && (<div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="secondary"
                      label={tMain("cancel")}
                      onClick={onBackButtonClick}
                    />
                  </div>)} */}
                  {/* <div className="w-24">
                  {thisOrgDriverGroupAllowed === "1" &&
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={userCanEdit ? handleEditDriver : () => setUserCanEdit(!userCanEdit)}
                      disabled={isFetchingSingleDriver || isLoadingEditDriver || (userCanEdit && !!Object.keys(errors).length) || getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER) < ROLE_ACCESS_LEVELS.WRITE}
                    />
                  }
                  </div> */}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleDriver || isLoadingEditDriver || isLoadingDeleteDriver || isLoadingEditDriverImage ? "opacity-40" : ""}`}
            >
              {/* <form onSubmit={handleSubmit}> */}
              <Accordian title={t("accord_details")} openByDefault={true}>
                <DriverGeneralDetailForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  uploadFormik={uploadFormik}
                  loadingData={
                    isFetchingSingleDriver ||
                    isLoadingEditDriverImage ||
                    !formikValuesReady
                  }
                />
              </Accordian>
              {thisOrgDriverGroupAllowed === "1" &&
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_DRIVER_LICENSE) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <Accordian title={t("accord_license_details")}>
                    <DriverLicenseDetailForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      formikSetValue={formik.setFieldValue}
                      handleBlur={handleBlur}
                      formikSetTouched={formik.setFieldTouched}
                      userCanEdit={
                        getUserACLAccessLevel(
                          ACL_NAMES.ACL_ADMIN_DRIVER_LICENSE
                        ) >= ROLE_ACCESS_LEVELS.WRITE && userCanEdit
                      }
                      loadingData={isFetchingSingleDriver || !formikValuesReady}
                    />
                  </Accordian>
                )}
              <Accordian title={t("accord_medical_and_other_details")}>
                <DriverMedicalDetailForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  formikSetValue={formik.setFieldValue}
                  handleBlur={handleBlur}
                  formikSetTouched={formik.setFieldTouched}
                  userCanEdit={userCanEdit}
                  loadingData={isFetchingSingleDriver || !formikValuesReady}
                />
              </Accordian>
              {/* </form> */}
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteDriver} />
    </>
  );
};

export default ScreenAdminDetailDriver;
